import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Table, Tooltip } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import "../../../assets/css/po2-design.css";

// Task 1900 (Feature 1861 / F3): the shared line-item table used by the vendor order
// builder, retailer review, and order detail/history screens - one implementation instead
// of the same columns being hand-rolled three times (per the spec's own §12.2 note).
//
// Line shape expected per row (matches PoOrderLineDetail from GET /{poId}/Detail, and the
// per-line objects UpsertOrderLine/RemoveOrderLine/UpdateOrderLineQuantity return):
//   { lineId, isAdHoc, adHocStyleNumber, adHocDescription, jewelryInventoryId,
//     listUnitPrice, quantity, discountType, discountValue, netUnitPrice, lineTotal }
//
// qtyEditable / discountEditable are separate props (not one "editable" flag) because the
// retailer review screen needs qty editable while price/discount stay locked - a single
// bool can't express that combination.
const DISCOUNT_TYPES = [
  { value: "NONE", glyph: "–", title: "No discount" },
  { value: "PERCENT", glyph: "%", title: "Percent off" },
  { value: "AMOUNT", glyph: "$", title: "Amount off" },
  { value: "OUT_THE_DOOR", glyph: "=", title: "Out-the-door target" },
];

// §7.2 order of operations, ported verbatim from PoPricing.CalcLine (PoPricing.cs:71-102) so
// a keystroke gets an instant, correct preview with zero network round trip. This is a
// LOCAL PREVIEW ONLY - the backend recomputes the same formula server-side on commit and is
// still the persisted source of truth, so float-vs-decimal drift here can never be saved.
function round2(v) {
  return (v < 0 ? -1 : 1) * Math.round(Math.abs(v) * 100) / 100;
}
function calcLine(listUnitPrice, quantity, discountType, discountValue) {
  const ext = (listUnitPrice || 0) * (quantity || 0);
  let total, net, residual = 0;
  switch (discountType) {
    case "PERCENT":
      total = round2(ext * (1 - (discountValue || 0) / 100));
      net = quantity ? round2(total / quantity) : 0;
      break;
    case "AMOUNT":
      total = ext - (discountValue || 0);
      net = quantity ? round2(total / quantity) : 0;
      break;
    case "OUT_THE_DOOR":
      total = discountValue || 0;
      net = quantity ? round2(total / quantity) : 0;
      residual = total - net * quantity;
      break;
    default:
      total = ext;
      net = listUnitPrice || 0;
  }
  if (total < 0) total = 0;
  return { extended: ext, netUnitPrice: net, lineTotal: total, residual, discount: ext - total };
}

const PoLineItemsTable = ({
  lines,
  loading,
  qtyEditable = false,
  discountEditable = false,
  showRemove = false,
  onQtyChange,
  onDiscountChange,
  onRemove,
  currencySign = "$",
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";

  const fmt = (v) => `${currencySign}${Number(v || 0).toFixed(2)}`;

  // Local-state-first editing (Epic 1857 UI-redesign, Phase 1): qty/discount inputs used to
  // call UpsertOrderLine/UpdateOrderLineQuantity on every single keystroke, replacing the
  // whole `lines` array from the network response mid-type - a real network-round-trip-per-
  // keystroke bug (worse on show-floor wifi), and the spec's own named hard requirement
  // ("editing a line must not re-render the page"). Fix: recompute totals instantly from
  // local state (calcLine above), commit to the backend only on blur or after a pause.
  const [localLines, setLocalLines] = useState(lines || []);
  const dirtyRef = useRef(new Set()); // lineIds with an uncommitted edit in flight
  const timersRef = useRef({}); // `${lineId}:${field}` -> timeout id
  const pendingCommitRef = useRef({}); // `${lineId}:${field}` -> commit fn

  useEffect(() => {
    setLocalLines((prev) =>
      (lines || []).map((incoming) => {
        if (dirtyRef.current.has(incoming.lineId)) {
          const mine = prev.find((p) => p.lineId === incoming.lineId);
          if (mine) return mine;
        }
        return incoming;
      })
    );
  }, [lines]);

  const scheduleCommit = (lineId, field, delayMs, commitFn) => {
    const key = `${lineId}:${field}`;
    dirtyRef.current.add(lineId);
    pendingCommitRef.current[key] = commitFn;
    if (timersRef.current[key]) clearTimeout(timersRef.current[key]);
    timersRef.current[key] = setTimeout(() => flushCommit(lineId, field), delayMs);
  };

  const flushCommit = (lineId, field) => {
    const key = `${lineId}:${field}`;
    if (timersRef.current[key]) {
      clearTimeout(timersRef.current[key]);
      delete timersRef.current[key];
    }
    const fn = pendingCommitRef.current[key];
    delete pendingCommitRef.current[key];
    if (fn) fn();
    // Only stop treating this line as "locally owned" once every field on it has committed.
    const stillPending = Object.keys(timersRef.current).some((k) => k.startsWith(`${lineId}:`));
    if (!stillPending) dirtyRef.current.delete(lineId);
  };

  const updateLocal = (lineId, patch) => {
    setLocalLines((prev) => prev.map((l) => (l.lineId === lineId ? { ...l, ...patch } : l)));
  };

  const withCalc = (line) => {
    const calc = calcLine(line.listUnitPrice, line.quantity, line.discountType, line.discountValue);
    return { ...line, netUnitPrice: calc.netUnitPrice, lineTotal: calc.lineTotal, _calc: calc };
  };

  const handleQtyInput = (line, raw) => {
    const quantity = Math.max(1, parseInt(raw, 10) || 1);
    updateLocal(line.lineId, { quantity });
    scheduleCommit(line.lineId, "qty", 600, () => onQtyChange && onQtyChange(line.lineId, quantity));
  };

  const handleDiscountType = (line, discountType) => {
    // Matches the prototype's own upLine(): switching to OUT_THE_DOOR defaults the target to
    // the current extended price (no discount by default); every other type resets to 0.
    const discountValue = discountType === "OUT_THE_DOOR" ? (line.listUnitPrice || 0) * (line.quantity || 0) : 0;
    updateLocal(line.lineId, { discountType, discountValue });
    // A button click, not a keystroke - commit immediately, no debounce needed.
    onDiscountChange && onDiscountChange(line.lineId, discountType, discountValue);
  };

  const handleDiscountValueInput = (line, raw) => {
    const discountValue = Number(raw.replace(/[^0-9.-]/g, "")) || 0;
    updateLocal(line.lineId, { discountValue });
    scheduleCommit(line.lineId, "discount", 600, () =>
      onDiscountChange && onDiscountChange(line.lineId, line.discountType, discountValue)
    );
  };

  const columns = [
    {
      title: t("Item"),
      key: "item",
      render: (_, line) => (
        <div>
          <div>
            <b>{line.isAdHoc ? line.adHocStyleNumber || t("Ad hoc") : line.styleNumber || line.jewelryInventoryId}</b>
            {line.isAdHoc && <span className="po2-tagwarn" style={{ marginInlineStart: 6 }}>{t("AD HOC")}</span>}
          </div>
          {line.isAdHoc && line.adHocDescription && <div className="po2-sub">{line.adHocDescription}</div>}
          {!line.isAdHoc && line.productName && <div className="po2-sub">{line.productName}</div>}
        </div>
      ),
    },
    {
      title: t("Qty"),
      key: "quantity",
      width: 90,
      className: "po2-r",
      render: (_, line) =>
        qtyEditable ? (
          <input
            className="po2-in po2-tnum"
            type="number"
            min={1}
            style={{ width: 62 }}
            value={line.quantity}
            onChange={(e) => handleQtyInput(line, e.target.value)}
            onBlur={() => flushCommit(line.lineId, "qty")}
          />
        ) : (
          line.quantity
        ),
    },
    {
      title: t("List Price"),
      key: "listUnitPrice",
      className: "po2-r",
      render: (_, line) => fmt(line.listUnitPrice),
    },
    {
      title: t("Discount"),
      key: "discount",
      width: 210,
      render: (_, line) => {
        const calc = calcLine(line.listUnitPrice, line.quantity, line.discountType, line.discountValue);
        if (!discountEditable) {
          return line.discountType && line.discountType !== "NONE" ? (
            <span>
              {DISCOUNT_TYPES.find((d) => d.value === line.discountType)?.title} ({fmt(line.discountValue)})
            </span>
          ) : (
            t("None")
          );
        }
        const adornClass = line.discountType === "PERCENT" ? "po2-suf" : "po2-pre";
        const adornSymbol = line.discountType === "PERCENT" ? "%" : "$";
        return (
          <div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span className="po2-dseg">
                {DISCOUNT_TYPES.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    title={t(d.title)}
                    className={line.discountType === d.value ? "po2-on" : ""}
                    onClick={() => handleDiscountType(line, d.value)}
                  >
                    {d.glyph}
                  </button>
                ))}
              </span>
              {line.discountType && line.discountType !== "NONE" && (
                <span className={`po2-adorn ${adornClass}`} style={{ flex: 1 }}>
                  <span>{adornSymbol}</span>
                  <input
                    className="po2-in po2-tnum"
                    style={{ width: 90 }}
                    value={line.discountValue}
                    onChange={(e) => handleDiscountValueInput(line, e.target.value)}
                    onBlur={() => flushCommit(line.lineId, "discount")}
                  />
                </span>
              )}
            </div>
            {line.discountType !== "NONE" && (
              <div className="po2-hint">
                {t("saves")} {fmt(calc.discount)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t("Net Unit"),
      key: "netUnitPrice",
      className: "po2-r",
      render: (_, line) => {
        const calc = withCalc(line)._calc;
        return (
          <span>
            {fmt(calc.netUnitPrice)}
            {calc.residual ? (
              <div className="po2-hint" title={t("Rounding: the quoted total does not divide evenly, so the odd cent goes on the first unit.")}>
                {t("unit 1")}: {fmt(calc.netUnitPrice + calc.residual)}
              </div>
            ) : null}
          </span>
        );
      },
    },
    {
      title: t("Line Total"),
      key: "lineTotal",
      className: "po2-r",
      render: (_, line) => <b>{fmt(withCalc(line)._calc.lineTotal)}</b>,
    },
  ];

  if (showRemove) {
    columns.push({
      title: "",
      key: "remove",
      width: 50,
      render: (_, line) => (
        <Tooltip title={t("Remove")}>
          <Button
            className="po2-btn po2-linky"
            style={{ color: "var(--po2-bad)" }}
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => onRemove && onRemove(line.lineId)}
          />
        </Tooltip>
      ),
    });
  }

  return (
    <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"} style={{ padding: 0, background: "transparent", border: "none" }}>
      <Table
        className="po2-lineTbl-wrap"
        rowKey="lineId"
        columns={columns}
        dataSource={localLines || []}
        loading={loading}
        pagination={false}
      />
    </div>
  );
};

export default PoLineItemsTable;
